<template>
    <div class="terms-and-consent">
        <div v-if="showConsentBlock" class="block">
            <div v-if="showEmailConsent" class="opt-in-wrapper">
                <div class="input-meta" v-if="userEmail">
                    <checkbox name="optInEmail" :label="emailLabelText" vuexKey="data"></checkbox>
                </div>
            </div>

            <div v-if="showSmsConsent" class="opt-in-wrapper">
                <div class="input-meta" v-if="userPhone">
                    <checkbox name="optInSms" :label="smsLabelText" vuexKey="data"></checkbox>
                </div>
            </div>

            <div v-if="showPhoneConsent" class="opt-in-wrapper">
                <div class="input-meta" v-if="userPhone">
                    <checkbox name="optInPhone" :label="phoneLabelText" vuexKey="data"></checkbox>
                </div>
            </div>

            <div v-if="showPostConsent" class="opt-in-wrapper">
                <div class="input-meta" v-if="userAddress">
                    <checkbox name="optInPost" :label="postLabelText" vuexKey="data"></checkbox>
                </div>
            </div>
        </div>

        <div v-if="termsAndConsentFields" class="consent-blocks">
            <div class="d-flex mt-3" v-if="termsAndConsentFields.length > 1 && this.territory === 'pol'">
                <div>
                    <checkbox
                        name="select_all"
                        :label="'<a>' + $t('form.fields.termsConsent.selectAll') + '</a>'"
                        @click="selectedAll"
                        :checked="allSelected"
                        vuexKey="data"
                    ></checkbox>
                </div>
            </div>

            <div v-for="(field, key) in termsAndConsentFields" :key="key">
                <div class="d-flex mt-3">
                    <div>
                        <checkbox
                            :name="getExtrasFieldValue(field, 'field_name')"
                            :label="field.content"
                            :ref="getExtrasFieldValue(field, 'field_name')"
                            :validationRules="isRequired(field)"
                            :customErrorMessage="errorMessage(field.extras)"
                            :inputHide="fieldHidden(field)"
                            @click="select"
                            vuexKey="data"
                        ></checkbox>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useStore } from 'vuex'
import { territory } from '@js/utilities//language.js'

// lodash components
import forEach from 'lodash/forEach'
import replace from 'lodash/replace'
import get from 'lodash/get'
import { useRoute } from 'vue-router'

export default {
    // static settings to initialize step (not reactive inside this file!)
    stepDefinition() {
        return {
            name: 'terms',
            excludeFromProgress: true,
            route: {
                absoluteUrl: process.env.TERRITORY === 'pol' ? `/uzyskaj-moje-wyniki` : `/terms`,
                name: 'apply.terms',
                meta: {
                    title: 'form.sections.terms.heading',
                    subText: 'form.sections.terms.subText',
                    alternativeSubText: {
                        partner: 'form.sections.terms.partnerSubText',
                        monevo: 'form.sections.terms.monevoSubText',
                    },
                    layoutType: 'large',
                },
            },
        }
    },

    data() {
        return {
            checked: {},
            allSelected: false,
        }
    },
    setup() {
        const store = useStore()
        const route = useRoute()

        return {
            store,
            route,
        }
    },
    created() {
        this.emitter.emit('stepLoaded')
    },
    beforeMount() {
        this.emitter.emit('updateSubText', {
            route: this.route,
            meta: 'subText',
            value: this.termsMeta,
            termMetaObjects: this.termMetaObjects,
        })

        if (this.partnerMarketingConsent) {
            let partnerFieldUpdates = {
                optInEmail: 'partner_opt_email',
                optInSms: 'partner_opt_sms',
            }

            forEach(partnerFieldUpdates, (key, field) => {
                this.store.commit('updateMappingKey', {
                    data: field,
                    mappedKey: key,
                })
            })
        }

        forEach(this.termsAndConsentFields, field => {
            const fieldName = this.getExtrasFieldValue(field, 'field_name')
            if (!(fieldName in this.store.state.apply.data)) {
                this.store.commit('addDynamicFields', fieldName)

                if (this.getExtrasFieldValue(field, 'submit_default_value') === '1' && this.fieldHidden(field)) {
                    this.checked[fieldName] = true

                    this.store.commit('setDataFieldValue', {
                        name: fieldName,
                        value: true,
                    })
                }
            }
        })
    },
    computed: {
        emailLabelText() {
            return this.getLabelText('form.fields.optEmail.sendTo', this.userEmail)
        },
        smsLabelText() {
            const countryCode = this.getLabelText('form.fields.optSms.countryCode')
            return this.getLabelText('form.fields.optSms.sendTo', countryCode + this.userPhone)
        },
        phoneLabelText() {
            const countryCode = this.getLabelText('form.fields.optSms.countryCode')
            return this.getLabelText('form.fields.optPhone.sendTo', countryCode + this.userPhone)
        },
        postLabelText() {
            return this.getLabelText('form.fields.optPost.sendTo', this.userAddress)
        },
        territory: () => territory(),
        userEmail: function () {
            return this.store.getters.fieldValue('emailAddress') || false
        },
        userPhone: function () {
            const mobileNumber = this.store.getters.fieldValue('mobileNumber')
            if (mobileNumber && mobileNumber.length >= 10) {
                return replace(mobileNumber, '0', '')
            }

            return mobileNumber || false
        },
        userAddress: function () {
            switch (process.env.TERRITORY) {
                case 'usa':
                    return [
                        this.store.getters.fieldValue('currentAddressStreetAddress'),
                        this.store.getters.fieldValue('currentAddressCity'),
                        this.store.getters.fieldValue('currentAddressState'),
                    ].join(',')

                case 'aus':
                    return [
                        this.store.getters.fieldValue('currentAddressStreetNumber'),
                        this.store.getters.fieldValue('currentAddressStreetName'),
                        this.store.getters.fieldValue('currentAddressSuburb'),
                        this.store.getters.fieldValue('currentAddressState'),
                    ].join(',')

                default:
                    return [
                        this.store.getters.fieldValue('currentAddressBuildingNumber'),
                        this.store.getters.fieldValue('currentAddressBuildingName'),
                        this.store.getters.fieldValue('currentAddressStreetName'),
                    ].join(',')
            }
        },
        termsAndConsentFields: function () {
            return this.store.state.global.data.content_blocks || false
        },
        showEmailConsent: function () {
            return parseInt(this.store.state.global.page.marketing_preferences_email) === 1 || false
        },
        showSmsConsent: function () {
            return parseInt(this.store.state.global.page.marketing_preferences_sms) === 1 || false
        },
        showPhoneConsent: function () {
            return parseInt(this.store.state.global.page.marketing_preferences_phone) === 1 || false
        },
        showPostConsent: function () {
            return parseInt(this.store.state.global.page.marketing_preferences_post) === 1 || false
        },
        showConsentBlock: function () {
            return this.showEmailConsent || this.showSmsConsent || this.showPhoneConsent || this.showPostConsent
        },
        partnerMarketingConsent: function () {
            return parseInt(this.store.state.global.data.partner_marketing_consent) === 1 || false
        },
        termsMeta: function () {
            let consent = this.showEmailConsent || this.showSmsConsent || this.showPhoneConsent || this.showPostConsent

            if (this.partnerMarketingConsent) {
                return consent && this.route.meta.alternativeSubText.partner
            }

            if (consent) {
                return this.route.meta.alternativeSubText.monevo
            }
        },
        termMetaObjects: function () {
            if (this.partnerMarketingConsent) {
                return {
                    partnerName: this.overwritePartnerName || window.APP_NAME,
                }
            }

            return {}
        },
        overwritePartnerName: function () {
            return this.store.state.global.page.partner_name ?? false
        },
    },
    methods: {
        getLabelText(labelKey, labelValue = '') {
            const label = this.$t(labelKey)
            if (labelValue) {
                return `<span style="color:#858585">${label}</span> <strong>${labelValue}</strong>`
            } else {
                return `${label}`
            }
        },
        isRequired: function (field) {
            const requiredObject = {
                checkboxRequired: true,
            }

            return parseInt(this.getExtrasFieldValue(field, 'consent_required')) === 1 ? requiredObject : null
        },
        errorMessage: function (extras) {
            return JSON.parse(extras).validation_message || null
        },
        getExtrasFieldValue: function (field, extrasFieldName) {
            return field?.extras ? get(JSON.parse(field.extras), extrasFieldName) : null
        },
        selectedAll: function () {
            for (const checkbox in this.$refs) {
                this.checked[checkbox] = !this.allSelected
                this.$refs[checkbox][0].check(!this.allSelected)
            }

            this.allSelected = !this.allSelected
        },
        select: function (event) {
            const fieldName = event?.target?.name

            this.checked[fieldName] = !this.checked[fieldName]
            this.allSelected = false
        },
        fieldHidden: function (field) {
            return parseInt(this.getExtrasFieldValue(field, 'field_hidden')) === 1
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/mixins/rem';

.block {
    background: #f4f5f6;
    border-radius: 5px;
    margin-top: 40px;
    padding: 30px;
    text-align: left;

    .opt-in-wrapper {
        &:not(:first-of-type) {
            margin-top: 40px;
        }

        .input-meta {
            color: var(--text-default);
            font-size: rem(14px);
            margin-left: 28px;

            strong {
                color: var(--text-dark);
            }
        }
    }
}

.consent-blocks {
    border-top: 1px solid #e0e1e5;
    margin-top: 20px;
    padding-top: 20px;

    .consent-field-text {
        font-size: rem(14px);
    }
}
</style>

<style lang="scss">
@import '~scss/mixins/rem';
.terms-and-consent {
    .opt-in-wrapper {
        height: 5px;

        label {
            > span {
                font-size: rem(14px);
                font-weight: bold;
            }
        }
    }

    .block {
        .checkbox-label {
            color: var(--text-dark);
        }
    }

    .checkbox-label {
        color: var(--input-text-color);
        font-size: rem(14px);
        line-height: rem(20px);
        text-align: left;

        p {
            color: var(--input-text-color);
        }

        a {
            color: var(--consent_hyperlink_color);
            font-weight: bold;
        }
    }
}
</style>
